import { createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import { IServiceProduct } from './typings'
import { getServiceProductList, getServiceProductListForTenant } from './api'
import { ILeadOppProduct } from '../leadOppProducts/typings'

export const serviceProductDisplayName = (
  s: IServiceProduct | ILeadOppProduct,
) => {
  if (s.description) {
    return s.description.trim() === '' ? s.name : `${s.name} - ${s.description}`
  }
  return s.name
}

const serviceProductAdapter = createEntityAdapter<IServiceProduct>({
  sortComparer: (a, b) =>
    serviceProductDisplayName(a).localeCompare(serviceProductDisplayName(b)),
})

export const initialState = {
  allServiceProducts: serviceProductAdapter.getInitialState(),
  tenantServiceProducts: new Map<string, IServiceProduct[]>(),
}

const serviceProducts = createSlice({
  name: 'serviceProducts',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getServiceProductList.fulfilled, (state, { payload }) => {
      serviceProductAdapter.setAll(state.allServiceProducts, payload)
    })

    builder.addCase(
      getServiceProductListForTenant.fulfilled,
      (state, { payload }) => {
        state.tenantServiceProducts = new Map() // Clear existing Map entries
        payload.forEach((service) => {
          const branchServices =
            state.tenantServiceProducts.get(service.branchId) || []

          branchServices.push(service)

          branchServices.sort((a, b) =>
            serviceProductDisplayName(a).localeCompare(
              serviceProductDisplayName(b),
            ),
          )

          state.tenantServiceProducts.set(service.branchId, branchServices)
        })
      },
    )
  },
})

export default serviceProducts.reducer

// Standard selectors for allServiceProducts remain the same
const allServiceProductSelectors =
  serviceProductAdapter.getSelectors<StoreData>(
    (state) => state.serviceProducts.allServiceProducts,
  )
export const { selectAll: selectAllServiceProducts } =
  allServiceProductSelectors

// Custom selector to get all tenant services across all branches
export const selectAllServiceProductsForTenant = (state: StoreData) => {
  return state.serviceProducts.tenantServiceProducts
}

// Custom selector to get tenant services by branchId
export const selectTenantServiceProductsByBranch = (
  state: StoreData,
  branchId: string,
) => {
  return state.serviceProducts.tenantServiceProducts.get(branchId) || []
}

export const aggregateServicesByLocationId = (
  locationId: string,
  services: ILeadOppProduct[],
) => services.filter((p) => p.serviceLocationId == locationId)
